<script setup lang="ts">
/**
 * The screen the owner is buying.
 *
 * One row per trading day, split by VAT rate and payment form — the shape
 * Pohoda posts a day in, which is why this is also the first artifact of
 * "close the books" rather than a reporting view built alongside it.
 *
 * THE GAP LIST IS AT THE TOP, not buried under the table. A missing trading day
 * blocks a period from being sealed, and a gap at the bottom of a scroll is a
 * gap nobody reads.
 */

import { computed } from "vue";
import StatTile from "./StatTile.vue";
import type { SalesReport } from "../api";
import { businessDate, money, rate, weekday } from "../format";

const props = defineProps<{
  report: SalesReport;
  locale: string;
  t: (key: string) => string;
}>();

const totals = computed(() => props.report.totals);

/** Rates present anywhere in the period, so the columns are stable. */
const rates = computed(() => {
  const seen = new Set<number>();
  for (const day of props.report.days) for (const line of day.taxLines) seen.add(line.rateBp);
  return [...seen].sort((a, b) => a - b);
});

const grossAt = (day: SalesReport["days"][number], rateBp: number): bigint =>
  day.taxLines.find((l) => l.rateBp === rateBp)?.grossMinor ?? 0n;
</script>

<template>
  <section>
    <div class="gf-tiles">
      <StatTile :label="t('sales.revenue')" :value="money(totals.dueMinor, locale)" />
      <StatTile :label="t('sales.vat')" :value="money(totals.vatMinor, locale)" />
      <StatTile :label="t('sales.checkCount')" :value="String(totals.checkCount)" />
      <StatTile :label="t('sales.average')" :value="money(totals.averageCheckMinor, locale)" />
      <StatTile
        v-if="totals.covers > 0"
        :label="t('sales.perCover')"
        :value="money(totals.perCoverMinor, locale)"
        :hint="t('sales.coversHint').replace('{n}', String(totals.covers))"
        data-testid="per-cover"
      />
      <StatTile
        v-if="totals.tipMinor > 0n"
        :label="t('sales.tips')"
        :value="money(totals.tipMinor, locale)"
        :hint="t('sales.tipsNotRevenue')"
      />
    </div>

    <div v-if="report.gaps.length > 0" class="gf-panel gf-panel--warn" role="alert">
      <h2 class="gf-panel__title">{{ t("sales.gaps") }}</h2>
      <p class="gf-note">{{ t("sales.gapsWhy") }}</p>
      <ul class="gf-chips">
        <li v-for="gap in report.gaps" :key="gap.businessDate" class="gf-chip gf-chip--warn">
          {{ weekday(gap.businessDate, locale) }} {{ businessDate(gap.businessDate, locale) }}
        </li>
      </ul>
    </div>

    <div class="gf-panel">
      <div class="gf-scroll">
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("sales.day") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.checkCount") }}</th>
              <th v-for="rateBp in rates" :key="rateBp" scope="col" class="gf-num">
                {{ rate(rateBp, locale) }}
              </th>
              <th scope="col" class="gf-num">{{ t("sales.vat") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.average") }}</th>
              <th v-if="totals.covers > 0" scope="col" class="gf-num">{{ t("sales.covers") }}</th>
              <th v-if="totals.covers > 0" scope="col" class="gf-num">{{ t("sales.perCover") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.revenue") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in report.days" :key="`${day.businessDate}-${day.cashPointId ?? ''}`">
              <th scope="row">
                <span class="gf-weekday">{{ weekday(day.businessDate, locale) }}</span>
                {{ businessDate(day.businessDate, locale) }}
              </th>
              <td class="gf-num">{{ day.checkCount }}</td>
              <td v-for="rateBp in rates" :key="rateBp" class="gf-num gf-muted">
                {{ money(grossAt(day, rateBp), locale) }}
              </td>
              <td class="gf-num gf-muted">{{ money(day.vatMinor, locale) }}</td>
              <td class="gf-num gf-muted">{{ money(day.averageCheckMinor, locale) }}</td>
              <td v-if="totals.covers > 0" class="gf-num gf-muted">{{ day.covers || "—" }}</td>
              <td v-if="totals.covers > 0" class="gf-num gf-muted">{{ day.covers ? money(day.perCoverMinor, locale) : "—" }}</td>
              <td class="gf-num gf-strong">{{ money(day.dueMinor, locale) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{{ t("common.total") }}</th>
              <td class="gf-num">{{ totals.checkCount }}</td>
              <td v-for="rateBp in rates" :key="rateBp" class="gf-num">
                {{ money(grossAt(totals, rateBp), locale) }}
              </td>
              <td class="gf-num">{{ money(totals.vatMinor, locale) }}</td>
              <td class="gf-num">{{ money(totals.averageCheckMinor, locale) }}</td>
              <td v-if="totals.covers > 0" class="gf-num">{{ totals.covers }}</td>
              <td v-if="totals.covers > 0" class="gf-num">{{ money(totals.perCoverMinor, locale) }}</td>
              <td class="gf-num gf-strong">{{ money(totals.dueMinor, locale) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="gf-panel">
      <h2 class="gf-panel__title">{{ t("sales.byTender") }}</h2>
      <!-- Scrolls in its own box: at phone width it pushed the page 10 px sideways. -->
      <div class="gf-scroll">
      <table class="gf-table">
        <thead>
          <tr>
            <th scope="col">{{ t("checks.tenders") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.checkCount") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.rounding") }}</th>
            <th scope="col" class="gf-num">{{ t("common.total") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tender in totals.tenders" :key="tender.kind">
            <th scope="row">{{ t(`tender.${tender.kind}`) }}</th>
            <td class="gf-num">{{ tender.count }}</td>
            <td class="gf-num gf-muted">{{ money(tender.roundingMinor, locale) }}</td>
            <td class="gf-num">{{ money(tender.amountMinor, locale) }}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  </section>
</template>
